<template>
  <Modal @close="$emit('close')">
    <template #icon>
      <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5 text-slate-400 shrink-0">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
    </template>

    <template #header>
      {{ $t('HeadContract.VariationsSchedule.VariationCommentModal.title') }} for
      <span class="">
        {{ variation.CodeRef ?? variation.description }}
      </span>
    </template>

    <template #body>
      <div class="flex flex-col h-full min-h-0">
        <form
          class="px-1"
          @submit.prevent="saveComment"
        >
          <label class="sr-only">
            {{ $t('HeadContract.VariationsSchedule.VariationCommentModal.label.comment') }}
          </label>

          <AppTextArea
            v-model="commentForm.form.comment"
            name="comment"
            rows="3"
            data-test="comment"
            class="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            :placeholder="$t('HeadContract.VariationsSchedule.VariationCommentModal.placeholder.comment')"
            :error="commentForm.getErrors('comment')"
          />

          <div class="mt-2 flex">
            <button
              type="submit"
              class="btn btn--primary-blue"
              data-test="update-record"
              :disabled="commentRecordAction.loading.value || !commentForm.form.comment?.trim()"
            >
              {{ $t('HeadContract.VariationsSchedule.VariationCommentModal.button.save') }}
            </button>
          </div>
        </form>

        <nav
          v-if="!isClientManager"
          class="mt-4 flex space-x-4 border-b border-slate-200 px-1"
          aria-label="Comment tabs"
        >
          <button
            type="button"
            class="py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200"
            :class="activeTab === 'internal'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'"
            title="Internal comments are visible to project team members"
            @click="activeTab = 'internal'"
          >
            {{ $t('HeadContract.VariationsSchedule.VariationCommentModal.tabs.internal') }}
          </button>

          <button
            type="button"
            class="py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200"
            :class="activeTab === 'external'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'"
            title="External comments are visible to project clients"
            @click="activeTab = 'external'"
          >
            {{ $t('HeadContract.VariationsSchedule.VariationCommentModal.tabs.external') }}
          </button>
        </nav>

        <div
          v-if="displayedComments.length"
          class="mt-4 overflow-y-auto w-full max-h-80 space-y-5"
        >
          <article
            v-for="comment in displayedComments"
            :key="comment.id"
            class="flex gap-3"
          >
            <div
              class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
              :class="avatarColor(comment.creator_id)"
              aria-hidden="true"
            >
              {{ initials(comment.creator?.name) }}
            </div>

            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span class="text-sm font-semibold text-slate-800">
                  {{ comment.creator?.name }}
                </span>

                <time
                  class="text-xs text-blue-600"
                  :datetime="comment.created_at"
                >
                  {{ formatCommentDate(comment.createdAt) }}
                </time>

                <span
                  v-if="comment.updated_at && comment.updated_at !== comment.created_at"
                  class="text-xs text-gray-500"
                >
                  (edited)
                </span>
              </div>

              <div
                v-if="editingCommentId !== comment.id"
                class="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 whitespace-pre-wrap break-words"
              >
                {{ comment.body }}
              </div>

              <form
                v-else
                class="mt-1"
                @submit.prevent="saveEditedComment(comment)"
              >
                <textarea
                  v-model="editForm.form.comment"
                  rows="3"
                  class="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  data-test="edit-comment"
                />

                <p
                  v-if="editForm.hasErrors('comment')"
                  class="mt-1 text-red-600 text-sm"
                >
                  {{ editForm.getErrors('comment') }}
                </p>

                <div class="mt-2 flex gap-2">
                  <button
                    type="submit"
                    class="btn btn--primary-blue"
                    :disabled="updateCommentAction.loading.value"
                  >
                    {{ $t('HeadContract.VariationsSchedule.VariationCommentModal.button.update') }}
                  </button>

                  <button
                    type="button"
                    class="btn btn--secondary-blue"
                    @click="cancelEdit"
                  >
                    {{ $t('HeadContract.VariationsSchedule.VariationCommentModal.button.cancel') }}
                  </button>
                </div>
              </form>

              <div
                v-if="editingCommentId !== comment.id && (canEdit(comment) || canDelete(comment))"
                class="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500"
              >
                <button
                  v-if="canEdit(comment)"
                  type="button"
                  class="hover:underline hover:text-slate-700"
                  @click="startEdit(comment)"
                >
                  {{ $t('HeadContract.VariationsSchedule.VariationCommentModal.button.edit') }}
                </button>

                <span
                  v-if="canEdit(comment) && canDelete(comment)"
                  aria-hidden="true"
                >·</span>

                <button
                  v-if="canDelete(comment)"
                  type="button"
                  class="hover:underline hover:text-red-600"
                  @click="confirmDelete(comment)"
                >
                  {{ $t('HeadContract.VariationsSchedule.VariationCommentModal.button.delete') }}
                </button>
              </div>
            </div>
          </article>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click.prevent="$emit('close')"
        >
          {{ $t('HeadContract.VariationsSchedule.VariationCommentModal.button.close') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import Comment from '@/models/Comment';
import User from '@/models/User';
import { useCommentsStore } from '@/store/comments';
import { useUserStore } from '@/store/user';
import { useVariationOrdersStore } from '@/store/variationOrders';
import Icon from '@/svgs/Icon.vue';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'vue3-toastify';

const AVATAR_COLORS = [
  'bg-blue-800',
  // 'bg-emerald-600',
  // 'bg-violet-600',
  // 'bg-amber-600',
  // 'bg-rose-600',
  // 'bg-cyan-700',
];

const props = defineProps<{
  projectId: Id;
  variationId: Id;
}>();

const emits = defineEmits<{
  (e: 'close');
}>();

const { t } = useI18n();
const userStore = useUserStore();
const commentsStore = useCommentsStore();
const variationOrdersStore = useVariationOrdersStore();
const isClientManager = computed(() => userStore.isClientManager());
const activeTab = ref<'internal' | 'external'>('internal');

const defaultIsInternal = () => {
  return isClientManager.value ? false : activeTab.value === 'internal';
};

const commentForm = useFormData({
  comment: '',
  is_internal: defaultIsInternal(),
});

watch(activeTab, () => {
  if(!isClientManager.value) {
    commentForm.form.is_internal = defaultIsInternal();
  }
});

const editForm = useFormData({
  comment: '',
});

const editingCommentId = ref<number | undefined>(undefined);

const isInternalComment = (comment: Comment) => {
  return comment.metadata?.is_internal !== false;
};

const variation = computed(() => {
  return variationOrdersStore.models.with('comments', (query) => {
    query.with('creator')
      .orderBy('created_at', 'desc');
  }).find(props.variationId);
});

const displayedComments = computed(() => {
  const comments = variation.value?.comments ?? [];

  if(isClientManager.value) {
    return comments;
  }

  if(activeTab.value === 'internal') {
    return comments.filter(isInternalComment);
  }

  return comments.filter((comment) => comment.metadata?.is_internal === false);
});

variationOrdersStore.fetchVariationOrder(props.variationId);

const commentRecordAction = useStoreApiAction(variationOrdersStore.comment);
const updateCommentAction = useStoreApiAction(commentsStore.updateComment);
const deleteCommentAction = useStoreApiAction(commentsStore.deleteComment);
const currentUserId = computed(() => (userStore.user as User | undefined)?.id);

const canEdit = (comment: Comment) => {
  return comment.creator_id === currentUserId.value;
};

const canDelete = (comment: Comment) => {
  return canEdit(comment) || userStore.isAdmin;
};

const initials = (name?: string) => {
  if(!name) {
    return '?';
  }

  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
};

const avatarColor = (creatorId: number) => {
  return AVATAR_COLORS[creatorId % AVATAR_COLORS.length];
};

const formatCommentDate = (date: Date) => {
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const saveComment = () => {
  commentForm.resetErrors();
  commentForm.form.is_internal = defaultIsInternal();

  commentRecordAction.request(variation.value.id, commentForm.form).then(() => {
    commentForm.resetData();
    commentForm.form.is_internal = defaultIsInternal();
  }).catch((error) => {
    commentForm.setErrorsFromAxiosError(error);
  });
};

const startEdit = (comment: Comment) => {
  editingCommentId.value = comment.id;
  editForm.resetErrors();

  editForm.setData({
    comment: comment.body,
  });
};

const cancelEdit = () => {
  editingCommentId.value = undefined;
  editForm.resetData();
  editForm.resetErrors();
};

const saveEditedComment = (comment: Comment) => {
  editForm.resetErrors();

  updateCommentAction.request(comment.id, { comment: editForm.form.comment }).then(() => {
    cancelEdit();
  }).catch((error) => {
    editForm.setErrorsFromAxiosError(error);
  });
};

const confirmDelete = (comment: Comment) => {
  Swal.fire({
    icon: 'warning',
    title: t('HeadContract.VariationsSchedule.VariationCommentModal.alert.deleteTitle'),
    text: t('HeadContract.VariationsSchedule.VariationCommentModal.alert.deleteText'),
    showCancelButton: true,
    confirmButtonText: t('HeadContract.VariationsSchedule.VariationCommentModal.button.delete'),
    confirmButtonColor: '#d33',
    cancelButtonText: t('HeadContract.VariationsSchedule.VariationCommentModal.button.cancel'),
  }).then((result) => {
    if(result.isConfirmed) {
      deleteCommentAction.request(comment.id).then(() => {
        if(editingCommentId.value === comment.id) {
          cancelEdit();
        }

        toast.success(t('HeadContract.VariationsSchedule.VariationCommentModal.toast.deleted'));
      });
    }
  });
};
</script>

<style scoped></style>
